import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { shiftBreakSchema, ShiftBreakFormData } from "@/schemas/shiftSchemas";
import { Shift } from "@/types/shifts";
import { useBreakTypes, useCreateShiftBreak, calculateBreakTime } from "@/hooks/useShifts";

interface ShiftBreakFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shift: Shift;
}

export function ShiftBreakFormDialog({
  open,
  onOpenChange,
  shift,
}: ShiftBreakFormDialogProps) {
  const { toast } = useToast();
  const { data: breakTypes } = useBreakTypes(true);
  const createMutation = useCreateShiftBreak();
  const isLoading = createMutation.isPending;

  const form = useForm<ShiftBreakFormData>({
    resolver: zodResolver(shiftBreakSchema),
    defaultValues: {
      shift_id: shift.id,
      break_type_id: "",
      start_offset_minutes: 120,
      duration_minutes: 30,
      is_mandatory: true,
    },
  });

  const startOffset = form.watch("start_offset_minutes");
  const selectedBreakTypeId = form.watch("break_type_id");

  // Auto-fill duration when break type is selected
  useEffect(() => {
    if (selectedBreakTypeId && breakTypes) {
      const breakType = breakTypes.find((bt) => bt.id === selectedBreakTypeId);
      if (breakType) {
        form.setValue("duration_minutes", breakType.default_duration_minutes);
      }
    }
  }, [selectedBreakTypeId, breakTypes, form]);

  useEffect(() => {
    if (open) {
      form.reset({
        shift_id: shift.id,
        break_type_id: "",
        start_offset_minutes: 120,
        duration_minutes: 30,
        is_mandatory: true,
      });
    }
  }, [open, shift.id, form]);

  const onSubmit = async (data: ShiftBreakFormData) => {
    try {
      await createMutation.mutateAsync(data);
      toast({
        title: "Descanso agregado",
        description: "El descanso se ha agregado al turno correctamente",
      });
      onOpenChange(false);
      form.reset();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Error al agregar el descanso",
        variant: "destructive",
      });
    }
  };

  const breakTime = calculateBreakTime(shift.start_time, startOffset);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Agregar Descanso al Turno</DialogTitle>
          <DialogDescription>
            Turno: {shift.name} ({shift.start_time.slice(0, 5)} - {shift.end_time.slice(0, 5)})
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="break_type_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de Descanso</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar tipo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {breakTypes?.map((bt) => (
                        <SelectItem key={bt.id} value={bt.id}>
                          {bt.name} ({bt.default_duration_minutes} min)
                          {bt.is_paid && " - Pagado"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="start_offset_minutes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Minutos desde inicio del turno</FormLabel>
                  <FormControl>
                    <Input type="number" min={0} max={720} {...field} />
                  </FormControl>
                  <FormDescription>
                    Hora del descanso: <strong>{breakTime}</strong>
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="duration_minutes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Duración (minutos)</FormLabel>
                  <FormControl>
                    <Input type="number" min={1} max={120} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_mandatory"
              render={({ field }) => (
                <FormItem className="flex items-center gap-2">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel className="!mt-0 cursor-pointer">
                    Descanso obligatorio
                  </FormLabel>
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Agregar
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
